/* Storystir gallery controls: one component for the home hero's demo and the
 * product page's gallery (brief §13). Arrows, a dot per slide, the "n / N" count,
 * swipe (native scroll-snap), and the arrow keys.
 *
 *   const gal = ssGallery(track, nav, { slide: '[data-slide]', label: s => '...' });
 *   gal.slides()   the visible slides, in order
 *   gal.index()    which one is in view
 *   gal.go(i)      scroll to it
 *   gal.mark()     refresh dots, count and arrow states (called on scroll)
 *   gal.rebuild()  rebuild the dots after the set changes (a slide hidden or shown)
 *   opts.onMark(i, slide)   called from mark(), for anything else that follows the slide
 *   opts.drag               drive the strip by transform instead of native scroll-snap:
 *                           an axis lock so a diagonal flick down the page never drags it
 *                           sideways, a settle on touchend AND touchcancel (iOS fires
 *                           touchcancel during momentum, which is how the strip came to
 *                           rest between slides on an iPhone), and slides placed by
 *                           percentage so a resize -- the Safari address bar collapsing --
 *                           cannot leave it part-way.
 *
 * The track is the scroll-snap container; a slide is any child matching opts.slide
 * that is not hidden. The nav is the snippet ss-gallery-nav. Nothing here depends on
 * either page's own markup.
 */
/* Pictures while they load (round 7): every picture in a slider, the gallery, the panel's cover or the review sheet that
   is still loading is held at opacity 0 (data-ss-fade="wait", storystir.css) and fades in once decoded; one already
   there is left alone. The frame is marked .is-base-in once its first photograph is in, and only then do the example
   names and the gold pill show. New pictures (the sheet's slides, swapped previews) are caught as they arrive. */
if (!window.ssFadeInit) {
  window.ssFadeInit = true;
  const SEL = '.ss-hero__media img, .ss-gallery__frame img, .ss-gallery__thumbs img, .ss-panel__book img, .ss-sheet__track img, .ss-bag__thumb';
  const settle = im => { im.dataset.ssFade = 'in'; };
  const watch = im => {
    if (im.dataset.ssFade) return;
    if (!im.getAttribute('src') && !im.currentSrc) { im.dataset.ssFade = 'idle'; return; }   // not asked for yet: shows when it is
    if (im.complete) { settle(im); return; }
    im.dataset.ssFade = 'wait';
    const done = () => { (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => settle(im)); };
    im.addEventListener('load', done, { once: true });
    im.addEventListener('error', () => settle(im), { once: true });
  };
  const baseIn = (frame, im) => {
    if (!frame) return;
    const on = () => frame.classList.add('is-base-in');
    if (!im || (im.complete && im.getAttribute('src'))) { on(); return; }
    im.addEventListener('load', on, { once: true }); im.addEventListener('error', on, { once: true });
    setTimeout(on, 8000);                                     // never held for long
  };
  const scan = root => (root.querySelectorAll ? root.querySelectorAll(SEL) : []).forEach(watch);
  const start = () => {
    scan(document);
    baseIn(document.querySelector('.ss-gallery__frame'), document.querySelector('.ss-gallery__slide .ss-h1__base, .ss-gallery__slide img'));
    baseIn(document.querySelector('.ss-hero--callout .ss-hero__media'), document.querySelector('[data-ss-hero-slide] .ss-callout__base img'));
    new MutationObserver(ms => ms.forEach(m => {
      if (m.type === 'attributes') { const im = m.target; if (im.matches && im.matches(SEL) && im.dataset.ssFade === 'idle') { delete im.dataset.ssFade; watch(im); } return; }
      m.addedNodes.forEach(n => { if (n.nodeType !== 1) return; if (n.matches && n.matches(SEL)) watch(n); scan(n); });
    })).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
}
if (!window.ssNavGuard) {
  window.ssNavGuard = true;
  document.addEventListener('click', e => {
    if (!window.ssNavAt || Date.now() - window.ssNavAt > 500) return;
    if (e.target.closest && e.target.closest('[data-gnav]')) return;
    if (e.target.closest && e.target.closest('a[data-ss-hero-booklink], [data-ss-hero-trypill], [data-ss-callout-go], [data-panel-open]')) {
      e.preventDefault(); e.stopImmediatePropagation();
    }
  }, true);
}
window.ssGallery = function (track, nav, opts) {
  opts = opts || {};
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sel = opts.slide || '[data-slide]';
  const dots = nav.querySelector('[data-gnav-dots]');
  const countEl = nav.querySelector('[data-gnav-count]');
  const prevBtn = nav.querySelector('[data-gnav-prev]');
  const nextBtn = nav.querySelector('[data-gnav-next]');

  const slides = () => Array.from(track.querySelectorAll(sel)).filter(s => !s.hidden);
  // a slide can be narrower than the track, so measure a slide rather than the track
  const step = () => { const s = slides()[0]; return (s && s.getBoundingClientRect().width) || track.clientWidth || 1; };
  const clamp = i => Math.max(0, Math.min(slides().length - 1, i));

  const drag = !!opts.drag;
  let cur = 0;
  if (drag) track.classList.add('is-drag');

  // Each SLIDE is moved by its own width -- translateX(-index x 100%) on the slide,
  // not on the track -- so the step is exactly one slide whatever the track measures,
  // and a resize re-lays it correctly because the unit is a percentage, not pixels.
  function apply(animate, px) {
    const shift = 'translateX(' + (px ? 'calc(' + (-100 * cur) + '% + ' + px + 'px)' : (-100 * cur) + '%') + ')';
    slides().forEach(s => {
      s.style.transition = (animate && !calm) ? 'transform .35s cubic-bezier(.22,.61,.36,1)' : 'none';
      s.style.transform = shift;
    });
  }

  const index = () => drag ? clamp(cur)
    : Math.max(0, Math.min(slides().length - 1, Math.round(track.scrollLeft / step())));

  function go(i, instant) {
    const n = clamp(i);
    if (drag) { cur = n; apply(!instant); mark(); return; }
    track.scrollTo({ left: n * step(), behavior: (calm || instant) ? 'auto' : 'smooth' });
  }
  function mark() {
    const s = slides(), i = index();
    dots.querySelectorAll('button').forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
    if (countEl) countEl.textContent = s.length ? (i + 1) + ' / ' + s.length : '';
    prevBtn.disabled = i <= 0;                                   // dimmed at either end: the way on is plain
    nextBtn.disabled = i >= s.length - 1;
    if (opts.onMark) opts.onMark(i, s[i]);
  }
  function rebuild() {
    const s = slides();
    dots.textContent = '';
    s.forEach((slide, k) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ss-gnav__dot';
      b.setAttribute('aria-label', opts.label ? opts.label(slide) : 'Show slide ' + (k + 1));
      b.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); go(k); });
      dots.appendChild(b);
    });
    nav.hidden = s.length < 2;
    if (drag) { cur = clamp(cur); apply(false); }
    else if (!s.some(x => x === slides()[index()])) track.scrollLeft = 0;
    mark();
  }
  // (round 6) the arrows and dots only ever change the slide: their presses go no further, and for half a second
  // after one a tap on anything that opens the personalization panel or the product page is ignored (an iOS
  // click can land late, on whatever the moving slide has just put there)
  prevBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); go(index() - 1); });
  nextBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); go(index() + 1); });
  ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend'].forEach(t => nav.addEventListener(t, e => {
    if (!e.target.closest('button')) return;
    window.ssNavAt = Date.now();
    e.stopPropagation();
  }, { passive: true }));
  track.addEventListener('keydown', e => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault(); go(index() + (e.key === 'ArrowRight' ? 1 : -1));
  });
  let raf = 0;
  track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(mark); }, { passive: true });

  if (drag) {
    const LOCK = 10;                     // px sideways before the strip takes the gesture
    let x0 = 0, y0 = 0, dx = 0, axis = null, w = 1;

    const settle = () => {
      if (axis !== 'x') { axis = null; return; }
      // never rest between slides, whatever ended the gesture
      cur = clamp(cur - Math.round(dx / (w || 1)));
      apply(true); mark();
      axis = null; dx = 0;
    };

    track.addEventListener('touchstart', e => {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      dx = 0; axis = null;
      const s0 = slides()[0];
      w = (s0 && s0.getBoundingClientRect().width) || track.clientWidth || 1;   // one slide, not the track
    }, { passive: true });

    track.addEventListener('touchmove', e => {
      if (e.touches.length !== 1) return;
      const mx = e.touches[0].clientX - x0, my = e.touches[0].clientY - y0;
      if (!axis) {
        // Clearly sideways, or it is the page's gesture and the strip does not move.
        if (Math.abs(mx) > LOCK && Math.abs(mx) > Math.abs(my)) axis = 'x';
        else if (Math.abs(my) > LOCK) { axis = 'y'; return; }
        else return;
      }
      if (axis !== 'x') return;
      e.preventDefault();                // ours now: stop the page scrolling with it
      dx = mx;
      apply(false, dx);
    }, { passive: false });

    track.addEventListener('touchend', settle);
    track.addEventListener('touchcancel', settle);   // iOS fires this during momentum
  }

  // A resize -- including Safari's address bar collapsing -- re-seats the strip on a
  // whole slide, with no animation.
  window.addEventListener('resize', () => { if (drag) apply(false); mark(); });
  rebuild();
  return { slides, index, go, mark, rebuild };
};
